// Thin React-side wrapper over the shared privacy store (public/privacy-store.js).
//
// The store itself is plain script rather than a module because the Consent Mode
// defaults and the Meta Pixel gate in index.html have to run long before the
// bundle is parsed. This file is just the door into it from React, so the app
// never keeps its own private copy of the visitor's choice.
//
// Nothing here throws. A failed preference read must not take a screen down with
// it, so every accessor degrades to the safe answer instead.

import { syncAttribution, syncConsent } from "./measurement";

const store = () => (typeof window === "undefined" ? null : window.QushiPrivacy);

export const readPrefs = () => {
  try {
    const P = store();
    return P ? P.read() : null;
  } catch (e) {
    return null;
  }
};

// True when the visitor is in a region where nonessential tags need consent
// BEFORE they may collect (EEA, UK, Switzerland).
export const isPriorConsentRegion = () => {
  try {
    const P = store();
    return P ? P.isPriorConsentRegion() : false;
  } catch (e) {
    return false;
  }
};

// True once the visitor has answered the consent banner, either way.
export const hasAnsweredBanner = () => {
  try {
    const P = store();
    return P ? P.hasAnsweredBanner() : false;
  } catch (e) {
    return false;
  }
};

// The sale/share opt-out, which a Global Privacy Control signal also triggers on
// its own. Advertising only - it never covers analytics.
export const isAdOptOut = () => {
  try {
    const P = store();
    return P ? P.adOptOut() : false;
  } catch (e) {
    return false;
  }
};

// Merge a change into the stored choice, push it to Consent Mode, and let any
// consent-gated tag that is still waiting start up.
export const savePrefs = (changes) => {
  try {
    const P = store();
    if (!P) return null;

    const adsBefore = P.consentState(P.read()).ad_storage === "granted";
    const prefs = { ...(P.read() || {}), ...changes, v: 1, updatedAt: Date.now() };
    P.write(prefs);
    const adsAfter = P.applyConsent(prefs).ad_storage === "granted";

    // The ad pixels in GTM wait on advertising consent, and GTM does not hold
    // a blocked tag back for later. When a visitor grants it mid-page, this
    // event lets those pixels start now rather than on the next page load.
    if (!adsBefore && adsAfter) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "ad_consent_granted" });
    }
    sendConsentToNative(prefs);
    // §8.1: every change also reaches the backend's consent ledger, and the
    // ad click references follow advertising consent (kept or deleted).
    syncConsent();
    syncAttribution();
    return prefs;
  } catch (e) {
    return null;
  }
};

// Inside the app, Firebase and AppsFlyer live in the native shell and gate on
// the same choice (Measurement Core §8.2), but the banner and the "Do Not Sell
// or Share" control are web screens. So every saved choice is handed to the
// shell over the existing 'Qushi' bridge as event "consent_changed".
//
// The shell can only act on a choice it has been told about, so the stored
// choice is also re-sent once per page load: a visitor who answered in an older
// build (before the shell listened) would otherwise never be asked again by the
// web banner and stay blocked in the shell forever.
const NATIVE_SURFACES = ["native", "native_legacy"];

export const sendConsentToNative = (prefs = readPrefs()) => {
  try {
    if (!prefs || !NATIVE_SURFACES.includes(window.QushiSurface?.surface)) return;

    const data = {
      analyticsConsent: typeof prefs.analyticsConsent === "boolean" ? prefs.analyticsConsent : null,
      adsConsent: typeof prefs.adsConsent === "boolean" ? prefs.adsConsent : null,
      doNotSellOrShare: typeof prefs.doNotSellOrShare === "boolean" ? prefs.doNotSellOrShare : null,
    };
    const send = () => window.flutter_inappwebview.callHandler("Qushi", { event: "consent_changed", data });

    // The bridge only takes calls after the plugin's ready event.
    // eslint-disable-next-line no-undef
    if (typeof isFlutterInAppWebViewReady !== "undefined" && isFlutterInAppWebViewReady) {
      send();
    } else {
      window.addEventListener("flutterInAppWebViewPlatformReady", send, { once: true });
    }
  } catch (e) {
    // A failed hand-off must never break the screen that saved the choice.
  }
};
